import { useCallback, useEffect, useState } from "react";
import JobLog from "../components/JobLog";
import { runMlAds, getMlAdsResultado } from "../api/client";

// Revisão do Mercado Ads (Product Ads) — só leitura. A API do ML não tem escrita documentada
// pra Product Ads, então tudo que aparece como "pausar" é feito no painel do Mercado Ads.

const brl = (v) =>
  v === null || v === undefined ? "—" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const roas = (v) => (v ? Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 2 }) + "x" : "—");
const fmtData = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");
const fmtDataHora = (iso) => (iso ? `${fmtData(iso)} ${iso.slice(11, 16)}` : "");

function Secao({ titulo, sub, children }) {
  return (
    <div className="bg-white rounded-xl shadow p-5 mb-5">
      <h3 className="font-bold text-gray-800">{titulo}</h3>
      {sub && <p className="text-xs text-gray-500 mt-0.5 mb-3">{sub}</p>}
      <div className={sub ? "" : "mt-3"}>{children}</div>
    </div>
  );
}

function Tabela({ colunas, linhas, vazio = "Nada aqui.", maxH = "max-h-[60vh]" }) {
  if (!linhas.length) return <p className="text-sm text-gray-400">{vazio}</p>;
  return (
    <div className={`overflow-auto ${maxH} scrollbar-visible`}>
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-gray-600 text-xs uppercase sticky top-0">
          <tr>{colunas.map((c) => <th key={c.k} className={`px-3 py-2 whitespace-nowrap ${c.num ? "text-right" : "text-left"}`}>{c.t}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {linhas.map((l, i) => (
            <tr key={i} className="hover:bg-gray-50">
              {colunas.map((c) => (
                <td key={c.k} className={`px-3 py-1.5 ${c.num ? "text-right whitespace-nowrap" : ""}`}>
                  {c.render ? c.render(l) : l[c.k] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ForaDeCampanha({ itens, campanhas }) {
  const [marcados, setMarcados] = useState(() => new Set());
  const [campanha, setCampanha] = useState("");
  const [copiado, setCopiado] = useState("");
  const [soHoje, setSoHoje] = useState(false);

  const hoje = new Date().toLocaleDateString("sv-SE"); // AAAA-MM-DD no fuso do navegador
  const lista = soHoje ? itens.filter((i) => i.criado_em === hoje) : itens;
  const todosMarcados = lista.length > 0 && lista.every((i) => marcados.has(i.item_id));
  const alternar = (id) => setMarcados((m) => { const n = new Set(m); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const alternarTodos = () => setMarcados((m) => {
    const n = new Set(m);
    lista.forEach((i) => (todosMarcados ? n.delete(i.item_id) : n.add(i.item_id)));
    return n;
  });

  const escolhidos = itens.filter((i) => marcados.has(i.item_id));
  const copiar = async () => {
    const texto = escolhidos.map((i) => i.item_id).join("\n");
    try { await navigator.clipboard.writeText(texto); setCopiado(`${escolhidos.length} MLB(s) copiados`); }
    catch { setCopiado("Não consegui copiar — selecione e copie a lista abaixo"); }
    setTimeout(() => setCopiado(""), 4000);
  };
  const nomeCampanha = campanhas.find((c) => String(c.id) === campanha)?.nome;

  return (
    <Secao
      titulo={`Fora do Mercado Ads — anúncios com estoque sem campanha ativa (${itens.length})`}
      sub="Mais novos primeiro. A API do Mercado Livre não permite adicionar anúncio a campanha: escolha aqui, copie os MLBs e inclua no painel do Mercado Ads (campanha → Adicionar anúncios).">
      <div className="flex flex-wrap items-center gap-3 mb-3 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={soHoje} onChange={(e) => setSoHoje(e.target.checked)} /> Só os cadastrados hoje
        </label>
        <select value={campanha} onChange={(e) => setCampanha(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5">
          <option value="">Escolha a campanha…</option>
          {campanhas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
        <button type="button" onClick={copiar} disabled={!escolhidos.length}
          className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold px-4 py-1.5 rounded-lg">
          Copiar {escolhidos.length || ""} MLB(s) pro painel
        </button>
        {copiado && <span className="text-green-700">{copiado}</span>}
      </div>
      {escolhidos.length > 0 && (
        <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-900">
          No painel: abra a campanha <strong>{nomeCampanha || "escolhida"}</strong> → Adicionar anúncios → busque/cole estes MLBs:
          <textarea readOnly value={escolhidos.map((i) => i.item_id).join("\n")} rows={Math.min(6, escolhidos.length)}
            className="mt-2 w-full font-mono text-xs border border-blue-200 rounded p-2 bg-white" />
        </div>
      )}
      <Tabela linhas={lista} vazio="Todos os anúncios com estoque já estão em campanha ativa." colunas={[
        { k: "sel", t: <input type="checkbox" checked={todosMarcados} onChange={alternarTodos} title="Marcar todos" />,
          render: (i) => <input type="checkbox" checked={marcados.has(i.item_id)} onChange={() => alternar(i.item_id)} /> },
        { k: "titulo", t: "Anúncio", render: (i) => <div className="flex items-center gap-2">
          {i.thumbnail && <img src={i.thumbnail} alt="" className="w-9 h-9 object-contain" />}
          <div>{i.permalink ? <a href={i.permalink} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">{i.titulo}</a> : i.titulo}
            <div className="text-xs text-gray-400">{i.item_id} · {i.tipo}{i.catalogo ? " · catálogo" : ""}</div></div></div> },
        { k: "criado_em", t: "Cadastro", render: (i) => <span className={i.criado_em === hoje ? "font-semibold text-green-700" : ""}>{fmtData(i.criado_em)}</span> },
        { k: "preco", t: "Preço", num: true, render: (i) => brl(i.preco) },
        { k: "estoque", t: "Estoque", num: true },
        { k: "situacao", t: "Situação", render: (i) => <>{i.situacao}{i.campanhas?.length > 0 && <div className="text-xs text-gray-400">{i.campanhas.join(", ")}</div>}</> },
      ]} />
    </Secao>
  );
}

const PAINEL = <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 whitespace-nowrap">Fazer no painel</span>;

export default function MercadoAds() {
  const [dias, setDias] = useState(30);
  const [jobId, setJobId] = useState(null);
  const [running, setRunning] = useState(false);
  const [r, setR] = useState(null);
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");

  const carregar = useCallback(() => {
    setLoading(true);
    getMlAdsResultado()
      .then(({ data }) => { setR(data); setErro(""); })
      .catch((e) => { setR(null); setErro(e.response?.status === 404 ? "" : e.response?.data?.detail || e.message); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const rodar = async () => {
    setErro("");
    setJobId(null);
    setRunning(true);
    try {
      const { data } = await runMlAds({ dias: Number(dias) || 30 });
      setJobId(data.job_id);
    } catch (e) {
      setRunning(false);
      setErro(e.response?.data?.detail || e.message);
    }
  };

  const filtra = (lista) => {
    const q = busca.trim().toLowerCase();
    return q ? lista.filter((x) => (x.titulo || "").toLowerCase().includes(q) || (x.item_id || "").toLowerCase().includes(q)) : lista;
  };

  const p = r?.principal;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Mercado Ads</h2>
          <p className="text-sm text-gray-500">
            Revisão do Product Ads (conta ESTANCIADOFUNKO). Só leitura: a API do Mercado Livre não permite pausar nem
            alterar campanhas, então as ações indicadas são feitas no painel do Mercado Ads.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-gray-500">Últimos dias</span>
            <input type="number" min={1} max={90} value={dias} onChange={(e) => setDias(e.target.value)}
              className="w-20 border border-gray-300 rounded-lg px-2 py-1.5" />
          </label>
          <button type="button" onClick={rodar} disabled={running}
            className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-semibold px-4 py-2 rounded-lg">
            {running ? "Rodando..." : "Rodar revisão"}
          </button>
        </div>
      </div>

      {erro && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{erro}</div>}
      {jobId && <div className="mb-5"><JobLog jobId={jobId} onDone={() => { setRunning(false); carregar(); }} /></div>}

      {loading && <p className="text-gray-400 text-sm">Carregando...</p>}
      {!loading && !r && !erro && <p className="text-gray-500 text-sm">Nenhuma revisão ainda — clique em "Rodar revisão".</p>}

      {r && (
        <>
          <p className="text-sm text-gray-600 mb-4">
            Período: <strong>{fmtData(r.janela?.[0])} a {fmtData(r.janela?.[1])}</strong> · gerado em {fmtDataHora(r.gerado_em)}
          </p>

          <ForaDeCampanha itens={r.fora_de_campanha || []} campanhas={r.campanhas_ativas || []} />

          <Secao titulo="Campanha principal — Black Friday" sub="Só conferência: não mexer em orçamento (R$ 50) nem em ROAS objetivo (5x).">
            {!p ? <p className="text-sm text-red-600">Campanha não encontrada na API.</p> : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 text-sm">
                  {[["Status", p.status], ["Orçamento", brl(p.orcamento)], ["ROAS objetivo", `${p.roas_target}x`],
                    ["Ajuste automático", p.orcamento_automatico ? "ligado" : "desligado"], ["Cliques", p.cliques],
                    ["Custo", brl(p.custo)], ["Vendas", p.vendas], ["ROAS", roas(p.roas)]].map(([k, v]) => (
                    <div key={k} className="bg-gray-50 rounded-lg px-3 py-2">
                      <div className="text-xs text-gray-500">{k}</div>
                      <div className="font-semibold text-gray-800">{v}</div>
                    </div>
                  ))}
                </div>
                <p className={`mt-3 text-sm ${p.confere ? "text-green-700" : "text-red-600"}`}>
                  {p.confere ? "✓ Confere com o esperado (ativa, R$ 50, ROAS 5x)." : "⚠ Diferente do esperado — conferir no painel."}
                </p>
              </>
            )}
          </Secao>

          <Secao titulo="Campanhas">
            <Tabela linhas={r.campanhas} colunas={[
              { k: "nome", t: "Campanha", render: (c) => <>{c.nome}{c.id && <div className="text-xs text-gray-400">{c.id}</div>}</> },
              { k: "status", t: "Status hoje" },
              { k: "plano", t: "Plano", render: (c) => <span className="flex items-center gap-2">{c.plano}{c.fazer_no_painel && PAINEL}</span> },
              { k: "cliques", t: "Cliques", num: true },
              { k: "custo", t: "Custo", num: true, render: (c) => brl(c.custo) },
              { k: "vendas", t: "Vendas", num: true },
              { k: "roas", t: "ROAS", num: true, render: (c) => (c.roas === null ? "—" : roas(c.roas)) },
            ]} />
          </Secao>

          <Secao
            titulo={`Pausar na Black Friday — anúncios que só gastam (${r.pausar_anuncios.length})`}
            sub={`Regra: ${r.regra.cliques_min}+ cliques e (nenhuma venda ou ROAS abaixo de ${r.regra.roas_min}x). Custo deles no período: ${brl(r.custo_pausar)}. Status HOLD = sem estoque (já não aparece).`}>
            <Tabela linhas={r.pausar_anuncios} vazio="Nenhum anúncio entra na regra." colunas={[
              { k: "titulo", t: "Anúncio" },
              { k: "status", t: "Status", render: (g) => <span className="flex items-center gap-2">{g.status}{g.status === "ACTIVE" && PAINEL}</span> },
              { k: "cliques", t: "Cliques", num: true },
              { k: "custo", t: "Custo", num: true, render: (g) => brl(g.custo) },
              { k: "vendas", t: "Vendas", num: true },
              { k: "roas", t: "ROAS", num: true, render: (g) => roas(g.roas) },
            ]} />
          </Secao>

          <div className="mb-3">
            <input type="text" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Filtrar buy box e reposição por título ou MLB…"
              className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>

          <Secao
            titulo={`Sem buy box — anúncios de catálogo ativos (${r.sem_buy_box.length})`}
            sub="Pra decidir preço. Cliques, vendas e ROAS são do produto inteiro (a API não separa mais por anúncio).">
            <Tabela linhas={filtra(r.sem_buy_box)} colunas={[
              { k: "titulo", t: "Anúncio", render: (a) => <>
                {a.permalink ? <a href={a.permalink} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">{a.titulo}</a> : a.titulo}
                <div className="text-xs text-gray-400">{a.item_id} · {a.campanha}</div></> },
              { k: "preco", t: "Preço", num: true, render: (a) => brl(a.preco) },
              { k: "cliques", t: "Cliques", num: true },
              { k: "vendas", t: "Vendas", num: true },
              { k: "roas", t: "ROAS", num: true, render: (a) => roas(a.roas) },
            ]} />
          </Secao>

          <Secao titulo={`Reposição — sem estoque que venderam no período (${r.reposicao.length})`} sub="Ordenado por vendas atribuídas.">
            <Tabela linhas={filtra(r.reposicao)} colunas={[
              { k: "titulo", t: "Anúncio", render: (g) => <>{g.titulo}<div className="text-xs text-gray-400">{g.campanha}</div></> },
              { k: "vendas", t: "Vendas", num: true },
              { k: "receita", t: "Receita", num: true, render: (g) => brl(g.receita) },
              { k: "cliques", t: "Cliques", num: true },
            ]} />
          </Secao>
        </>
      )}
    </div>
  );
}
